import React, { useCallback, useState } from 'react';
import {
  IconButton,
  IconButtonProps,
} from '@lifeomic/chroma-react/components/IconButton';
import { Plus } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { CreateTemplatingItemModal } from '../Modals/CreateTemplatingItemModal';
import { Folder, TemplatingItemDetail } from '../../models';
import { useSelectedTemplatingItem } from '../../hooks/useSelectedTemplatingItem';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { useIntl, defineMessages } from 'react-intl';
import { clsx } from 'clsx';

const messages = defineMessages({
  createTemplatingItem: {
    id: 'CreateTemplatingItemButton.Text.createTemplatingItem',
    defaultMessage: 'Create Template or Composite Report Template',
  },
});

const useStyles = makeStyles(() => ({
  createTemplatingItemButton: {
    padding: 0,
  },
}));

type BaseCreateTemplatingItemProps = {
  folder?: Folder;
  onSuccess?: (templatingItem: TemplatingItemDetail) => void;
  onDismiss?: () => void;
};

type CreateTemplatingItemTextButtonProps = BaseCreateTemplatingItemProps;

type CreateTemplatingItemIconButtonProps = BaseCreateTemplatingItemProps &
  Partial<IconButtonProps> & {
    className?: string;
  };

const useCreateTemplatingItemLogic = (
  onSuccessProp?: (templatingItem: TemplatingItemDetail) => void,
  onDismissProp?: () => void,
) => {
  const intl = useIntl();
  const [isCreatingTemplatingItem, setIsCreatingTemplatingItem] =
    useState(false);
  const { setSelectedTemplatingItemId } = useSelectedTemplatingItem();

  const handleSuccess = useCallback(
    (templatingItem: TemplatingItemDetail) => {
      setIsCreatingTemplatingItem(false);
      setSelectedTemplatingItemId(templatingItem.id);
      onSuccessProp?.(templatingItem);
    },
    [setSelectedTemplatingItemId, onSuccessProp],
  );

  const handleDismiss = useCallback(() => {
    setIsCreatingTemplatingItem(false);
    onDismissProp?.();
  }, [onDismissProp]);

  const openModal: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();
      setIsCreatingTemplatingItem(true);
    },
    [],
  );

  const createTemplatingItemLabel = intl.formatMessage(
    messages.createTemplatingItem,
  );

  return {
    isCreatingTemplatingItem,
    handleSuccess,
    handleDismiss,
    openModal,
    createTemplatingItemLabel,
  };
};

const CreateTemplatingItemTextButton: React.FC<
  CreateTemplatingItemTextButtonProps
> = ({ folder, onSuccess, onDismiss }) => {
  const { formatMessage } = useIntl();
  const {
    isCreatingTemplatingItem,
    handleSuccess,
    handleDismiss,
    openModal,
    createTemplatingItemLabel,
  } = useCreateTemplatingItemLogic(onSuccess, onDismiss);

  return (
    <>
      <Button onClick={openModal} aria-label={createTemplatingItemLabel}>
        {formatMessage(messages.createTemplatingItem)}
      </Button>
      <CreateTemplatingItemModal
        folder={folder}
        isOpen={isCreatingTemplatingItem}
        onSuccess={handleSuccess}
        onDismiss={handleDismiss}
      />
    </>
  );
};

const CreateTemplatingItemIconButton: React.FC<
  CreateTemplatingItemIconButtonProps
> = ({ folder, className, onSuccess, onDismiss, ...props }) => {
  const styles = useStyles();
  const {
    isCreatingTemplatingItem,
    handleSuccess,
    handleDismiss,
    openModal,
    createTemplatingItemLabel,
  } = useCreateTemplatingItemLogic(onSuccess, onDismiss);

  return (
    <>
      <IconButton
        {...props}
        size={0}
        icon={Plus}
        aria-label={createTemplatingItemLabel}
        onClick={openModal}
        className={clsx(className, styles.createTemplatingItemButton)}
      />
      <CreateTemplatingItemModal
        folder={folder}
        isOpen={isCreatingTemplatingItem}
        onSuccess={handleSuccess}
        onDismiss={handleDismiss}
      />
    </>
  );
};

export const CreateTemplatingItemButton = {
  Text: CreateTemplatingItemTextButton,
  Icon: CreateTemplatingItemIconButton,
};
